import type { StoreId } from './brands.js';
import type { IngestConfig } from './store-definition.js';

export type StoreType = 'file' | 'repo' | 'web';
export type StoreStatus = 'ready' | 'indexing' | 'error';

/**
 * Current schema version for store manifests.
 * Increment when making breaking changes to store format.
 *
 * Version history:
 * - 1: Initial version (pre-embedding-function)
 * - 2: Added modelId for embedding provenance tracking
 * - 3: Added pathType for portable relative paths
 */
export const CURRENT_SCHEMA_VERSION = 3;

/**
 * Path storage type for file and repo stores.
 * - 'relative': Path is relative to projectRoot, portable across machines
 * - 'absolute': Path is absolute, used for paths outside projectRoot
 * - undefined: Pre-v3 store, treated as 'absolute'
 */
export type PathType = 'relative' | 'absolute';

interface BaseStore {
  readonly id: StoreId;
  readonly name: string;
  readonly description?: string | undefined;
  readonly tags?: readonly string[] | undefined;
  readonly status?: StoreStatus | undefined;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  /**
   * Schema version for detecting incompatible stores.
   * Stores with schemaVersion < CURRENT_SCHEMA_VERSION require reindex.
   */
  readonly schemaVersion: number;
  /**
   * Embedding model ID used to index this store.
   * Used for provenance tracking and mismatch detection.
   */
  readonly modelId: string;
}

export interface FileStore extends BaseStore {
  readonly type: 'file';
  readonly path: string;
  readonly pathType?: PathType | undefined;
  readonly ingest?: IngestConfig | undefined;
}

export interface RepoStore extends BaseStore {
  readonly type: 'repo';
  readonly path: string;
  readonly pathType?: PathType | undefined;
  readonly url?: string | undefined;
  readonly branch?: string | undefined;
  readonly depth?: number | undefined;
  readonly ingest?: IngestConfig | undefined;
}

export interface WebStore extends BaseStore {
  readonly type: 'web';
  readonly url: string;
  readonly depth: number;
  readonly maxPages?: number | undefined;
  readonly crawlInstructions?: string | undefined;
  readonly extractInstructions?: string | undefined;
}

export type Store = FileStore | RepoStore | WebStore;

export function isFileStore(store: Store): store is FileStore {
  return store.type === 'file';
}

export function isRepoStore(store: Store): store is RepoStore {
  return store.type === 'repo';
}

export function isWebStore(store: Store): store is WebStore {
  return store.type === 'web';
}
